// TCM Five Element constitution quiz — the Five Elements framework is an ancient,
// public-domain tradition (like Ayurveda's doshas). Original wording, non-diagnostic.
// 14 questions across 4 dimensions. Tally scoring → 5 elements.
const tcmElement = {
  slug: "tcm-element",
  title: "What's your Five Element type?",
  emoji: "🌳",
  blurb: "A 14-question check to reveal your Chinese-medicine element — Wood, Fire, Earth, Metal, or Water.",
  seoTitle: "Five Element Quiz: Your Chinese Medicine Type | Sarv",
  seoDescription:
    "Take the free Five Element quiz to discover your Traditional Chinese Medicine constitution — Wood, Fire, Earth, Metal, or Water — and the herbs traditionally linked to each.",
  keywords:
    "five element quiz, TCM element type, Chinese medicine constitution, wood fire earth metal water quiz, TCM body type",
  intro:
    "14 questions across temperament, body, rhythms, and emotions. The Five Elements are a traditional lens — general wellness only, not a medical diagnosis.",
  evidenceNote:
    "The Five Elements (Wu Xing) are a roughly 3,000-year-old framework at the heart of Traditional Chinese Medicine, mapping temperament and body patterns to Wood, Fire, Earth, Metal, and Water. It's a lens for self-understanding explored in the research literature and recognized by the WHO's global traditional-medicine work — not a diagnostic test, and not a replacement for conventional care.",
  references: [
    {
      label: "Traditional Chinese Medicine: What You Need To Know",
      publisher: "NCCIH / NIH",
      url: "https://www.nccih.nih.gov/health/traditional-chinese-medicine-what-you-need-to-know",
    },
    {
      label: "The psychosomatic traits of 'people with the Five Elements' in TCM: a qualitative study",
      publisher: "PubMed / NIH",
      url: "https://pubmed.ncbi.nlm.nih.gov/38098326/",
    },
    {
      label: "Global Traditional Medicine Strategy 2025–2034",
      publisher: "World Health Organization",
      url: "https://www.who.int/publications/i/item/9789240113176",
    },
    {
      label: "Astragalus: Usefulness and Safety",
      publisher: "NCCIH / NIH",
      url: "https://www.nccih.nih.gov/health/astragalus",
    },
    {
      label: "The effect of astragalus on humoral and cellular immune response: a systematic review & meta-analysis",
      publisher: "PubMed / NIH",
      url: "https://pubmed.ncbi.nlm.nih.gov/37952511/",
    },
  ],
  scoring: { mode: "tally" },
  questions: [
    // --- Temperament ---
    {
      section: "Temperament",
      question: "Under stress, you tend to…",
      options: [
        { label: "Get frustrated or irritable and push through", scores: { wood: 1 } },
        { label: "Get restless, talkative, or scattered", scores: { fire: 1 } },
        { label: "Worry, overthink, and ruminate", scores: { earth: 1 } },
        { label: "Withdraw and turn quiet or wistful", scores: { metal: 1 } },
        { label: "Feel fearful, cautious, or drained", scores: { water: 1 } },
      ],
    },
    {
      section: "Temperament",
      question: "Your natural energy is best described as…",
      options: [
        { label: "Driven and goal-focused", scores: { wood: 1 } },
        { label: "Warm, expressive, and sociable", scores: { fire: 1 } },
        { label: "Steady, nurturing, and grounded", scores: { earth: 1 } },
        { label: "Precise, orderly, and disciplined", scores: { metal: 1 } },
        { label: "Reflective, deep, and reserved", scores: { water: 1 } },
      ],
    },
    {
      section: "Temperament",
      question: "How do others describe your personality?",
      options: [
        { label: "Assertive and driven", scores: { wood: 1 } },
        { label: "Warm and enthusiastic", scores: { fire: 1 } },
        { label: "Kind and dependable", scores: { earth: 1 } },
        { label: "Reserved and principled", scores: { metal: 1 } },
        { label: "Calm and deep", scores: { water: 1 } },
      ],
    },
    // --- Body & symptoms ---
    {
      section: "Body & symptoms",
      question: "Physically, you lean toward…",
      options: [
        { label: "Tension, tight muscles, or headaches", scores: { wood: 1 } },
        { label: "Feeling warm, flushed, or overheated", scores: { fire: 1 } },
        { label: "Digestive issues or a feeling of heaviness", scores: { earth: 1 } },
        { label: "Dry skin, lungs, or sinus sensitivity", scores: { metal: 1 } },
        { label: "Feeling cold, with low-back or knee weakness", scores: { water: 1 } },
      ],
    },
    {
      section: "Body & symptoms",
      question: "Which describes your sleep?",
      options: [
        { label: "Restless, waking around 1–3am, vivid dreams", scores: { wood: 1 } },
        { label: "Hard to fall asleep — my mind is buzzing", scores: { fire: 1 } },
        { label: "I sleep, but wake heavy and unrefreshed", scores: { earth: 1 } },
        { label: "Light sleeper, wake easily, dry throat", scores: { metal: 1 } },
        { label: "Wake to urinate, feel cold, deeply tired", scores: { water: 1 } },
      ],
    },
    {
      section: "Body & symptoms",
      question: "Where do you tend to feel tension or symptoms?",
      options: [
        { label: "Neck, shoulders, jaw, or eyes", scores: { wood: 1 } },
        { label: "Chest, palpitations, or flushing", scores: { fire: 1 } },
        { label: "Stomach and digestion; heavy limbs", scores: { earth: 1 } },
        { label: "Lungs, sinuses, or skin", scores: { metal: 1 } },
        { label: "Low back, knees, ears; feeling cold", scores: { water: 1 } },
      ],
    },
    // --- Rhythms & appetite ---
    {
      section: "Rhythms & appetite",
      question: "Which season feels most like 'you'?",
      options: [
        { label: "Spring — new growth and momentum", scores: { wood: 1 } },
        { label: "Summer — warmth and connection", scores: { fire: 1 } },
        { label: "Late summer — harvest and abundance", scores: { earth: 1 } },
        { label: "Autumn — clarity and letting go", scores: { metal: 1 } },
        { label: "Winter — rest and stillness", scores: { water: 1 } },
      ],
    },
    {
      section: "Rhythms & appetite",
      question: "What's your relationship with food?",
      options: [
        { label: "Irregular appetite, worse when frustrated", scores: { wood: 1 } },
        { label: "Strong appetite; I love variety and spice", scores: { fire: 1 } },
        { label: "I comfort-eat and crave sweets and routine", scores: { earth: 1 } },
        { label: "Modest appetite; I prefer light, simple food", scores: { metal: 1 } },
        { label: "Low or erratic appetite; I crave warming food", scores: { water: 1 } },
      ],
    },
    {
      section: "Rhythms & appetite",
      question: "What's your ideal time of day?",
      options: [
        { label: "Early morning — I'm ready to go", scores: { wood: 1 } },
        { label: "Midday — peak social energy", scores: { fire: 1 } },
        { label: "Afternoon — steady and productive", scores: { earth: 1 } },
        { label: "Evening — I wind down and reflect", scores: { metal: 1 } },
        { label: "Night — quietly alive, or craving rest", scores: { water: 1 } },
      ],
    },
    // --- Emotions & needs ---
    {
      section: "Emotions & needs",
      question: "Emotionally, what's your default?",
      options: [
        { label: "Impatient, driven, easily frustrated", scores: { wood: 1 } },
        { label: "Excitable and warm; I seek stimulation", scores: { fire: 1 } },
        { label: "Caring; I worry about others", scores: { earth: 1 } },
        { label: "Reserved; I value order and feel grief deeply", scores: { metal: 1 } },
        { label: "Introspective, cautious, easily fearful", scores: { water: 1 } },
      ],
    },
    {
      section: "Emotions & needs",
      question: "When out of balance, you most feel…",
      options: [
        { label: "Anger or frustration", scores: { wood: 1 } },
        { label: "Anxiety or over-excitement", scores: { fire: 1 } },
        { label: "Worry or over-attachment", scores: { earth: 1 } },
        { label: "Grief or rigidity", scores: { metal: 1 } },
        { label: "Fear or exhaustion", scores: { water: 1 } },
      ],
    },
    {
      section: "Emotions & needs",
      question: "Which idea resonates most?",
      options: [
        { label: "Growth, ambition, and movement", scores: { wood: 1 } },
        { label: "Joy, passion, and connection", scores: { fire: 1 } },
        { label: "Stability, care, and home", scores: { earth: 1 } },
        { label: "Structure, refinement, and release", scores: { metal: 1 } },
        { label: "Wisdom, depth, and stillness", scores: { water: 1 } },
      ],
    },
    {
      section: "Emotions & needs",
      question: "What do you most need in your life right now?",
      options: [
        { label: "A healthy outlet for drive and frustration", scores: { wood: 1 } },
        { label: "Calm and steadiness amid the buzz", scores: { fire: 1 } },
        { label: "Nourishment and better boundaries", scores: { earth: 1 } },
        { label: "Space to breathe and let go", scores: { metal: 1 } },
        { label: "Deep rest and rebuilding", scores: { water: 1 } },
      ],
    },
  ],
  results: {
    wood: {
      name: "Wood",
      emoji: "🌳",
      tagline: "Driven, decisive, and growth-oriented.",
      summary:
        "As a Wood type, your nature is driven, decisive, and growth-oriented — in Chinese medicine, Wood is associated with the Liver and the smooth, free flow of energy (qi) through body and mind. At your best you're visionary, purposeful, and quick to act. When Wood energy gets 'stuck' or overheated — often through frustration, long hours, and no outlet — you may notice tension headaches, tight muscles, irritability, or a sense of being blocked. The traditional aim is to keep things moving and flowing: regular movement, flexibility of both body and plans, and herbs classically linked to the Liver and smooth flow. The deeper work for Wood is learning to channel that considerable drive without letting frustration build up behind it.",
      focus: [
        "Movement and flexibility — physical and mental — to keep energy flowing.",
        "Herbs traditionally linked to the Liver and smooth flow in TCM.",
        "Channeling drive without burning out on frustration.",
      ],
      herbs: ["Milk thistle", "Dandelion", "Peppermint"],
      categorySlug: "liver-and-detoxification",
    },
    fire: {
      name: "Fire",
      emoji: "🔥",
      tagline: "Warm, expressive, and full of spark.",
      summary:
        "As a Fire type, you're warm, expressive, and naturally connective — Fire is associated in Chinese medicine with the Heart and the Shen, or spirit, which governs joy, sleep, and a settled mind. In balance you're charismatic and radiant, drawn to people and experiences. When Fire flares too high, that same energy can tip into restlessness, overstimulation, anxiety, palpitations, or trouble sleeping as the mind stays 'lit.' The traditional approach is about tending the flame rather than fueling it: protecting genuine warmth and connection while building in cooling, calming routines and real rest. Herbs classically linked to the Heart and a settled spirit are used to help an over-bright Fire find a steadier, more sustainable glow.",
      focus: [
        "Protect calm and connection — Fire thrives on genuine warmth, not overstimulation.",
        "Herbs traditionally linked to the Heart and a settled spirit in TCM.",
        "Cooling routines and rest to temper an over-bright flame.",
      ],
      herbs: ["Hawthorn", "Rose", "Lemon balm"],
      categorySlug: "neurological-and-cognitive-health",
    },
    earth: {
      name: "Earth",
      emoji: "🌾",
      tagline: "Grounded, nurturing, and steady.",
      summary:
        "As an Earth type, you're grounded, nurturing, and dependable — Earth is associated in Chinese medicine with the Spleen and Stomach, the organs of digestion, nourishment, and how well you 'transform' food and experience into usable energy. In balance you're stable, caring, and a steadying presence for others. When Earth is taxed — often by overthinking, worry, or over-giving — you may notice digestive heaviness, sluggishness, cravings, or a tendency to ruminate. The traditional focus is on nourishment and steadiness: warm, well-cooked meals, regular routines, and herbs classically linked to the Spleen and Stomach. For Earth types, learning to set boundaries and receive care — not only give it — is often the missing piece.",
      focus: [
        "Warm, well-cooked meals and steady routines to support digestion.",
        "Herbs traditionally linked to the Spleen and Stomach in TCM.",
        "Setting boundaries so you don't over-give and deplete yourself.",
      ],
      herbs: ["Ginger", "Licorice root", "Fennel"],
      categorySlug: "digestive-health",
    },
    metal: {
      name: "Metal",
      emoji: "⚪",
      tagline: "Precise, principled, and refined.",
      summary:
        "As a Metal type, you value clarity, order, and quality — Metal is associated in Chinese medicine with the Lungs and Large Intestine, and with the capacity to take in what nourishes and let go of what doesn't. At your best you're disciplined, discerning, and principled. When Metal is strained you may feel rigid or perfectionistic, dry (in skin, sinuses, or lungs), or weighed down by grief and a difficulty releasing. The traditional approach centers on the breath and on healthy release: breathwork and fresh air to support the Lungs, and herbs classically linked to the Lungs and immunity. The deeper practice for Metal is letting go — of clutter, of control, and of what you've outgrown — so you can make room to breathe.",
      focus: [
        "Breathwork and fresh air to support the Lungs.",
        "Herbs traditionally linked to the Lungs and immunity in TCM.",
        "Practicing release — of clutter, control, and old grief.",
      ],
      herbs: ["Astragalus", "Mullein", "Thyme"],
      categorySlug: "respiratory-health",
    },
    water: {
      name: "Water",
      emoji: "💧",
      tagline: "Deep, reflective, and enduring.",
      summary:
        "As a Water type, you're deep, reflective, and quietly enduring — Water is associated in Chinese medicine with the Kidneys and with Jing, the body's foundational reserves of vitality that we draw on across a lifetime. In balance you're wise, calm, and resourceful under pressure. When Water runs low — often from overwork, chronic stress, or too little true rest — you may feel fearful, cold, worn down, or notice low-back and knee weakness. The traditional focus is conservation and restoration: genuine rest, warmth, and gentle strength-building, alongside herbs classically linked to the Kidneys and deep vitality. For Water types, protecting your reserves rather than constantly spending them is the very heart of staying well.",
      focus: [
        "Rest and conserve — Water governs your deep reserves.",
        "Herbs traditionally linked to the Kidneys and vitality in TCM.",
        "Warmth and gentle strength-building to protect your core energy.",
      ],
      herbs: ["Ashwagandha", "Nettle", "Rehmannia"],
      categorySlug: "urinary-and-kidney-health",
    },
  },
};

export default tcmElement;
